import { useState, useCallback } from "react"
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    TextFieldProps,
} from "@mui/material"
import { observer } from "mobx-react"
import { t } from "@lingui/macro"
import { DatePicker } from "@mui/lab"

import {
    getYesterdayDate,
    getYesterdayDateObject,
    formatDateToYYYYMMDD,
    getTodayDateObject,
    getTodayDate,
} from "./helpers"

import type { ILoadInvoicesModalProps } from "./types"

import { useStore } from "src/store/lib/useStore"
import { GlobalStore } from "src/store"
import { reportSuccess, reportUnhandledApiError } from "src/lib/report"

export const LoadInvoicesModal = observer(
    ({ propertyOwnerId, store }: ILoadInvoicesModalProps) => {
        const gstore = useStore(GlobalStore)
        const [selectedFromDate, setSelectedFromDate] = useState<string>(
            getYesterdayDate(),
        )
        const [selectedToDate, setSelectedToDate] = useState<string>(
            getTodayDate(),
        )

        const handleClose = () => {
            gstore.modals.pop()
        }

        const handleSubmit = async () => {
            if (selectedFromDate.length > 0 && selectedToDate.length > 0) {
                try {
                    await store.loadInvoices(
                        propertyOwnerId,
                        selectedFromDate,
                        selectedToDate,
                    )
                    reportSuccess(t`row-action-renderer-fast-api.load-success`)
                    handleClose()
                } catch (e) {
                    reportUnhandledApiError(e)
                }
            }
        }

        const renderDatePickerInput = useCallback(
            (props: TextFieldProps) => (
                <TextField
                    {...props}
                    sx={{ width: "100%", mt: 2 }}
                    inputProps={{
                        ...props.inputProps,
                        readOnly: true,
                    }}
                />
            ),
            [],
        )

        return (
            <Stack padding="8px">
                <DialogTitle>
                    {t`row-action-renderer-fast-api.load-invoices`}
                </DialogTitle>
                <DialogContent>
                    <DatePicker
                        label={t`global.from-date`}
                        value={selectedFromDate}
                        onChange={(newValue) => {
                            if (newValue != null) {
                                setSelectedFromDate(
                                    formatDateToYYYYMMDD(newValue),
                                )
                            }
                        }}
                        maxDate={getYesterdayDateObject()}
                        renderInput={renderDatePickerInput}
                    />
                    <DatePicker
                        label={t`global.to-date`}
                        value={selectedToDate}
                        onChange={(newValue) => {
                            if (newValue != null) {
                                setSelectedToDate(
                                    formatDateToYYYYMMDD(newValue),
                                )
                            }
                        }}
                        maxDate={getTodayDateObject()}
                        minDate={
                            selectedFromDate !== "" && selectedFromDate != null
                                ? new Date(selectedFromDate)
                                : undefined
                        }
                        renderInput={renderDatePickerInput}
                    />
                </DialogContent>
                <DialogActions>
                    <Button color="info" onClick={handleClose}>
                        {t`global.cancel`}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="info"
                        disabled={
                            selectedFromDate.length === 0 ||
                            selectedToDate.length === 0
                        }
                    >
                        {t`global.load`}
                    </Button>
                </DialogActions>
            </Stack>
        )
    },
)
